// src/components/Gallery/Lightbox/LightboxModal.js
import React, { useState, useEffect } from 'react';
import { DynamicImageLightbox, DynamicVideo } from '../MediaComponents';
import LightboxControls from './LightboxControls';
import LightboxInfo from './LightboxInfo';
import { preloadImage } from '../../../utils/imageLoader';

const LightboxModal = ({ imageIndex, onClose, onNext, onPrev, allArtworks }) => {
  const item = allArtworks[imageIndex];
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Preload adjacent images
  useEffect(() => {
    const preloadAdjacent = () => {
      const nextIndex = (imageIndex + 1) % allArtworks.length;
      const prevIndex = (imageIndex - 1 + allArtworks.length) % allArtworks.length;
      
      // Preload next and previous images
      [nextIndex, prevIndex].forEach(index => {
        const adjacentItem = allArtworks[index];
        if (adjacentItem && !adjacentItem.mediaType) {
          // Preload in background without waiting
          preloadImage(`full/${adjacentItem.imagePath}`);
        }
      });
    };

    // Small delay to let current image start loading first
    const preloadTimer = setTimeout(preloadAdjacent, 100);
    return () => clearTimeout(preloadTimer);
  }, [imageIndex, allArtworks]);

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };

    // Prevent background scrolling
    document.body.style.overflow = 'hidden';

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      // Restore background scrolling
      document.body.style.overflow = 'auto';
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onNext, onPrev]);

  const { width, height } = dimensions;
  const isMobile = width < 768;
  const isTrueLandscape = width > height && height < 500;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // Light palette background
        backgroundColor: 'rgba(248, 249, 250, 0.98)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0',
        backdropFilter: 'blur(10px)'
      }}
      onClick={onClose}
    >
      {/* Controls Component */}
      <LightboxControls
        imageIndex={imageIndex}
        totalImages={allArtworks.length}
        onClose={onClose}
        onNext={onNext}
        onPrev={onPrev}
        isMobile={isMobile}
        isTrueLandscape={isTrueLandscape}
      />

      {/* Main Content Container */}
      {isTrueLandscape ? (
        // Fullscreen layout for true landscape
        <>
          {/* Full-screen image */}
          {item.mediaType === 'video' ? (
            <DynamicVideo
              imagePath={item.imagePath}
              controls
              autoPlay
              muted
              loop
              style={{
                maxWidth: '100vw',
                maxHeight: '100vh',
                objectFit: 'contain'
              }}
            />
          ) : (
            <DynamicImageLightbox
              key={`fullscreen-light-${item.id}-${imageIndex}`}
              imagePath={`full/${item.imagePath}`}
              alt={item.title}
              style={{
                maxWidth: '100vw',
                maxHeight: '100vh',
                objectFit: 'contain',
                borderRadius: '0'
              }}
            />
          )}

          {/* Bottom info bar for fullscreen */}
          <LightboxInfo
            artwork={item}
            isMobile={isMobile}
            isTrueLandscape={isTrueLandscape}
          />
        </>
      ) : (
        // Standard layout for portrait/desktop
        <div
          style={{
            maxWidth: isMobile ? '95vw' : '85vw',
            maxHeight: '95vh',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '10px' : '20px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Image Container */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              maxWidth: '100%'
            }}
          >
            {item.mediaType === 'video' ? (
              <DynamicVideo
                imagePath={item.imagePath}
                controls
                autoPlay
                muted
                loop
                style={{
                  maxWidth: '100%',
                  maxHeight: isMobile ? '65vh' : '75vh',
                  borderRadius: '8px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                  display: 'block'
                }}
              />
            ) : (
              <DynamicImageLightbox
                key={`standard-${item.id}-${imageIndex}`}
                imagePath={`full/${item.imagePath}`} 
                alt={item.title}
                style={{
                  maxWidth: '100%',
                  maxHeight: isMobile ? '65vh' : '75vh',
                  borderRadius: '8px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                  display: 'block'
                }}
              />
            )}
          </div>

          {/* Artwork Info */}
          <LightboxInfo
            artwork={item}
            isMobile={isMobile}
            isTrueLandscape={false}
          />
        </div>
      )}
    </div>
  );
};

export default LightboxModal;